/**
 * PATH: packages/email-hub/src/feature/email-layout/AddMailboxNavRow.tsx
 * FILE_ID: EMAIL-LAYOUT-ADD-MAILBOX-001
 * PURPOSE: Render the shared add-mailbox action in each navigation shape.
 */

import { MailPlus } from 'lucide-react'

interface AddMailboxNavRowProps {
  variant: 'rail' | 'rail-collapsed' | 'pill'
  open: boolean
  setOpen: (value: boolean) => void
  onMicrosoft: () => void
  onGoogle: () => void
}

export function AddMailboxNavRow({
  variant,
  open,
  setOpen,
  onMicrosoft,
  onGoogle,
}: AddMailboxNavRowProps) {
  const pickMicrosoft = () => {
    setOpen(false)
    onMicrosoft()
  }
  const pickGoogle = () => {
    setOpen(false)
    onGoogle()
  }

  if (variant === 'rail-collapsed') {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          title="Add a Microsoft or Google mailbox"
          aria-label="Add mailbox"
          className="flex items-center justify-center h-9 w-9 mx-auto rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <MailPlus className="w-4 h-4" />
        </button>
        {open && (
          <div className="flex flex-col items-center gap-0.5">
            <button
              type="button"
              onClick={pickMicrosoft}
              title="Sign in with a Microsoft account"
              className="h-7 w-7 rounded-md border border-blue-500/30 bg-blue-500/10 text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-500/20"
            >
              M
            </button>
            <button
              type="button"
              onClick={pickGoogle}
              title="Sign in with a Google account"
              className="h-7 w-7 rounded-md border border-red-500/30 bg-red-500/10 text-[10px] font-semibold text-red-600 dark:text-red-400 hover:bg-red-500/20"
            >
              G
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              title="Cancel"
              aria-label="Cancel add mailbox"
              className="h-5 w-7 text-[10px] text-muted-foreground/70 hover:text-foreground"
            >
              ×
            </button>
          </div>
        )}
      </div>
    )
  }

  if (variant === 'pill') {
    if (open) {
      return (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={pickMicrosoft}
            title="Sign in with a Microsoft account"
            className="flex items-center gap-1.5 rounded-lg border border-blue-500/30 bg-blue-500/10 px-2.5 py-1.5 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-500/20 transition-colors"
          >
            <MailPlus className="w-3 h-3" /> Microsoft
          </button>
          <button
            type="button"
            onClick={pickGoogle}
            title="Sign in with a Google account"
            className="flex items-center gap-1.5 rounded-lg border border-red-500/30 bg-red-500/10 px-2.5 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-500/20 transition-colors"
          >
            <MailPlus className="w-3 h-3" /> Google
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Cancel"
            className="text-xs text-muted-foreground/70 hover:text-foreground px-1 shrink-0"
          >
            Cancel
          </button>
        </div>
      )
    }
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Add another mailbox (Microsoft or Google)"
        className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground transition-colors shrink-0"
      >
        <MailPlus className="w-3.5 h-3.5" />
        <span>Add mailbox</span>
      </button>
    )
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        title="Add a Microsoft or Google mailbox"
        className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
      >
        <MailPlus className="w-4 h-4" />
        <span>Add mailbox</span>
      </button>
      {open && (
        <div className="mt-0.5 ml-9 flex items-center gap-1.5">
          <button
            type="button"
            onClick={pickMicrosoft}
            title="Sign in with a Microsoft account"
            className="text-[11px] px-2 py-0.5 rounded border border-blue-500/30 bg-blue-500/10 text-blue-600 dark:text-blue-400 hover:bg-blue-500/20"
          >
            Microsoft
          </button>
          <button
            type="button"
            onClick={pickGoogle}
            title="Sign in with a Google account"
            className="text-[11px] px-2 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-600 dark:text-red-400 hover:bg-red-500/20"
          >
            Google
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            title="Cancel"
            aria-label="Cancel add mailbox"
            className="text-[11px] text-muted-foreground/70 hover:text-foreground px-1"
          >
            ×
          </button>
        </div>
      )}
    </div>
  )
}
